import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchNotifications as fetchNotificationsService,
  markNotificationAsRead as markNotificationAsReadService,
} from "../services/notificationService";

const toErrorMessage = (err, fallback) => {
  if (!err) return fallback;
  if (typeof err === "string") return err;
  return err?.message || fallback;
};

// Thunk: Fetch notifications
export const fetchNotificationsAsync = createAsyncThunk(
  "notifications/fetchAll",
  async (_, { rejectWithValue }) => {
    try {
      const data = await fetchNotificationsService();

      // Backend may return an array OR wrapped { items: [] }
      if (Array.isArray(data)) return data;
      if (Array.isArray(data?.items)) return data.items;

      return [];
    } catch (error) {
      return rejectWithValue(toErrorMessage(error, "Failed to load notifications."));
    }
  }
);

// Thunk: Mark notification as read
export const markNotificationAsReadAsync = createAsyncThunk(
  "notifications/markAsRead",
  async (notificationId, { rejectWithValue }) => {
    try {
      if (!notificationId) return rejectWithValue("Missing notificationId");

      await markNotificationAsReadService(notificationId);
      return notificationId; // reducer updates local state
    } catch (error) {
      return rejectWithValue(toErrorMessage(error, "Failed to mark notification as read."));
    }
  }
);

const initialState = {
  items: [],
  loading: false,
  error: null,
};

const notificationsSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    clearNotificationErrors(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch notifications
      .addCase(fetchNotificationsAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNotificationsAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload || [];
      })
      .addCase(fetchNotificationsAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to load notifications.";
      })

      // Mark as read
      .addCase(markNotificationAsReadAsync.fulfilled, (state, action) => {
        const id = action.payload;
        state.items = state.items.map((n) => {
          const nid = n.id || n.notificationId;
          return nid === id ? { ...n, read: true, isRead: true } : n;
        });
      })
      .addCase(markNotificationAsReadAsync.rejected, (state, action) => {
        state.error = state.error || action.payload || "Failed to mark notification as read.";
      });
  },
});

export const { clearNotificationErrors } = notificationsSlice.actions;

// Safe selectors
export const selectNotifications = (state) => state.notifications?.items || [];
export const selectNotificationsLoading = (state) => state.notifications?.loading || false;
export const selectNotificationsError = (state) => state.notifications?.error || null;

export default notificationsSlice.reducer;
